import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Fingerprint, BookOpen, Sparkles, Eye, Brain, Zap, RefreshCw, ArrowRight, FileText, MessageSquare, Layers, Target } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Accordion } from '../components/ui/Accordion';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import {
  Aurora,
  ScrollVeil,
  FloatingParticles,
  CursorGlow,
  MagneticCard,
  GlowBorder,
  SectionDivider,
  AnimatedNumber,
  Reveal,
  StaggerGroup,
} from '../components/ui/depth';
import { fadeUpItem, fadeRightItem, fadeLeftItem, scaleItem } from '../components/ui/depth-motion';
import { Footer } from '../components/Footer';
import { Header } from '../components/ui/Header';
import { THICK_TILE_STYLE } from '../components/dashboard/tileStyles';
import { ParticleFingerprint } from '../components/dashboard/ParticleFingerprint';

const dimensions = [
  'How you prefer to take in information',
  'How you engage with new material',
  'How deeply you need to go',
  'What sequence clicks',
  'How much detail you need at once',
  'What motivates you',
  'How you engage with different perspectives',
  'How what you already know connects to new learning',
];

const miniFaq = [
  {
    question: 'Is my content safe and private?',
    answer:
      'Yes. Your uploaded content is processed by AI to generate your learning experience but is not stored permanently or used to train AI models. CogniPrint is built with privacy by design, compliant with both POPIA and GDPR. Your content stays yours.',
  },
  {
    question: 'Is there a free tier?',
    answer:
      'Your quick CogniPrint profile is free — always. The 2-minute diagnostic costs nothing and gives you genuine insight into how you learn across 4 core dimensions. Upgrade to unlock all 8 dimensions and for the full experience — content uploads, Professor Cogni (your personal AI tutor), and personalised learning paths — plans start at $9.99/month.',
  },
  {
    question: 'Does it work on mobile?',
    answer:
      'Yes. CogniPrint is fully responsive on mobile browsers, and a native app is in development. Study on your phone during your commute, review concept maps between classes, chat with Professor Cogni, your personal AI tutor, anywhere.',
  },
  {
    question: 'How is this different from ChatGPT or NotebookLM?',
    answer:
      'Those tools give everyone the same output regardless of how they learn. CogniPrint starts with your CogniPrint profile — a map of how your brain processes information — and builds every learning experience around it. Professor Cogni knows your content, your profile, and where you are in the material. It\'s not a generic chatbot. It\'s a personalised study partner.',
  },
];

/* ───────────────────────── HERO ───────────────────────── */
function Hero() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const midY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  // The hero opens directly on the ParticleFingerprint assembly — there is no
  // intro video, so content reveals immediately on mount.
  const introDone = true;

  // Interactive cognitive-fingerprint orb (same component as the dashboard).
  const orbRef = useRef<HTMLDivElement>(null);
  const [cursorBias, setCursorBias] = useState<{ x: number; y: number } | null>(null);
  const [clickAction, setClickAction] = useState<{ key: number; count: number } | null>(null);
  // Gates the orb's mount so its scatter→converge→ignite assembly plays at the
  // moment the hero is revealed rather than behind the intro.
  const [orbReady, setOrbReady] = useState(false);
  // Increments to re-fire the flash-bloom + expanding shockwave-ring overlays
  // at each climax of the entrance choreography.
  const [shockwave, setShockwave] = useState(0);
  // Flips true once the orb has fully settled into its ambient state; triggers
  // the background glow cloud to gracefully shrink away to nothing.
  const [settled, setSettled] = useState(false);

  // ─── "Big bang" entrance choreography ──────────────────────────────────
  // Timeline (wall-clock from page mount; orb mounts at 220ms so its internal
  // assembly elapsed ≈ wall − 220ms):
  //   0.22s  orb mounts → particles stream in from all directions
  //   2.8s   core ignites (internal) + ignition flash/rings
  //   3.0s   SUPERNOVA fires (count 5): vacuum → compress → starburst beams
  //   4.2s   starburst beams shoot → big flash + shockwave rings synced to it
  //   7.8s   supernova reforms → final implosion pulse settles it
  // Reduced-motion: assemble quietly, no bursts/overlays.
  useEffect(() => {
    const timers: number[] = [];
    timers.push(window.setTimeout(() => setOrbReady(true), 220));
    if (!prefersReducedMotion) {
      timers.push(window.setTimeout(() => setShockwave((n) => n + 1), 2800)); // ignition flash
      timers.push(window.setTimeout(() => setClickAction({ key: 1, count: 5 }), 3000)); // supernova
      timers.push(window.setTimeout(() => setShockwave((n) => n + 1), 4200)); // starburst flash + rings
      timers.push(window.setTimeout(() => setClickAction({ key: 2, count: 1 }), 7800)); // settle pulse
      // After the settle pulse fully calms (~10s), retire the glow cloud.
      timers.push(window.setTimeout(() => setSettled(true), 10200));
    } else {
      // Reduced motion: quiet assembly only, then retire the glow cloud.
      timers.push(window.setTimeout(() => setSettled(true), 3500));
    }
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const el = orbRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setCursorBias({
        x: (e.clientX - centerX) / (rect.width / 2),
        y: (e.clientY - centerY) / (rect.height / 2),
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const fireBurst = () =>
    setClickAction((prev) => ({
      key: (prev?.key ?? 0) + 1,
      count: ((prev?.key ?? 0) % 5) + 1,
    }));

  return (
    <section ref={ref} className="relative overflow-hidden min-h-screen flex flex-col -mt-24">
      <RidgeTexture />
      <Aurora palette="mixed" />

      <motion.div className="absolute inset-0 pointer-events-none" style={{ y: bgY }}>
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[120px]" />
        <div className="absolute top-1/3 left-[60%] w-[400px] h-[400px] bg-gold-500/8 rounded-full blur-[100px]" />
      </motion.div>

      <motion.div className="absolute inset-0 pointer-events-none" style={{ y: midY }}>
        {[
          { x: '12%', y: '20%', size: 3, opacity: 0.3, delay: 0 },
          { x: '85%', y: '35%', size: 4, opacity: 0.2, delay: 1.5 },
          { x: '8%', y: '65%', size: 3, opacity: 0.25, delay: 0.8 },
          { x: '92%', y: '55%', size: 2, opacity: 0.2, delay: 2 },
          { x: '20%', y: '80%', size: 3, opacity: 0.15, delay: 1.2 },
          { x: '75%', y: '15%', size: 2, opacity: 0.2, delay: 0.5 },
          { x: '35%', y: '12%', size: 3, opacity: 0.15, delay: 1.8 },
          { x: '65%', y: '85%', size: 2, opacity: 0.2, delay: 0.3 },
        ].map((dot, i) => (
          <motion.div
            key={`node-${i}`}
            className="absolute rounded-full bg-indigo-400"
            style={{ left: dot.x, top: dot.y, width: dot.size, height: dot.size, opacity: dot.opacity }}
            animate={{ opacity: [dot.opacity, dot.opacity * 2.5, dot.opacity], scale: [1, 1.3, 1] }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut', delay: dot.delay }}
          />
        ))}
        <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.06 }}>
          <motion.line x1="12%" y1="20%" x2="35%" y2="12%" stroke="#6C5CE7" strokeWidth="1"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 0.5 }} />
          <motion.line x1="75%" y1="15%" x2="85%" y2="35%" stroke="#6C5CE7" strokeWidth="1"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 0.8 }} />
          <motion.line x1="8%" y1="65%" x2="20%" y2="80%" stroke="#6C5CE7" strokeWidth="1"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 1.2 }} />
          <motion.line x1="85%" y1="35%" x2="92%" y2="55%" stroke="#6C5CE7" strokeWidth="1"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 1 }} />
        </svg>
      </motion.div>


      <motion.div className="flex-1 flex items-center px-6 sm:px-10 lg:px-16 relative pt-24" style={{ y: textY, opacity: heroOpacity }}>
        <div className="max-w-5xl mx-auto text-center">
          <motion.h1
            className="font-heading font-extrabold text-4xl sm:text-[3.5rem] lg:text-[4rem] leading-[1.08] tracking-[-0.035em] mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Your brain doesn't learn like everyone else's.
            <br />
            <span className="bg-gradient-to-r from-indigo-400 via-indigo-300 to-gold-500 bg-clip-text text-transparent animate-gradient-flow">
              Your learning tools should know that.
            </span>
          </motion.h1>

          <motion.p
            className="text-lg sm:text-xl text-content-secondary max-w-2xl mx-auto mb-10 font-body"
            initial={{ opacity: 0, y: 20 }}
            animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Upload anything you're studying. CogniPrint profiles how you learn, then rebuilds your content to match — with Professor Cogni, your AI tutor that adapts to you.
          </motion.p>

          <motion.div
            className="relative mx-auto mb-10"
            initial={{ opacity: 0, scale: 0.2, y: 20, rotate: -160 }}
            animate={introDone ? { opacity: 1, scale: 1, y: 0, rotate: 0 } : { opacity: 0, scale: 0.2, y: 20, rotate: -160 }}
            transition={{ type: 'spring', stiffness: 70, damping: 11, delay: 0.18 }}
            style={{ width: 360, height: 360 }}
          >
            {/* Slow-rotating aurora halo behind the orb — energetic backdrop
                during the entrance, then shrinks away once the orb settles. */}
            <motion.div
              className="absolute inset-[-45%] rounded-full pointer-events-none"
              style={{ background: 'conic-gradient(from 0deg, rgba(108,92,231,0.18), rgba(240,165,0,0.14), rgba(80,200,255,0.16), rgba(108,92,231,0.18))', filter: 'blur(38px)' }}
              animate={settled ? { rotate: 360, scale: 0, opacity: 0 } : { rotate: 360, scale: 1, opacity: 1 }}
              transition={{
                rotate: { duration: 22, repeat: Infinity, ease: 'linear' },
                scale: { duration: 6, ease: 'easeInOut' },
                opacity: { duration: 6, ease: 'easeInOut' },
              }}
            />
            {/* Radial bloom that flares as the fingerprint ignites, settles,
                then shrinks to nothing once the orb reaches its ambient state. */}
            <motion.div
              className="absolute inset-[-30%] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(108,92,231,0.10) 0%, transparent 65%)' }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={
                settled
                  ? { opacity: 0, scale: 0 }
                  : orbReady
                    ? { opacity: [0, 1, 0.65], scale: [0.6, 1.15, 1] }
                    : { opacity: 0, scale: 0.6 }
              }
              transition={
                settled
                  ? { duration: 6, ease: 'easeInOut' }
                  : { duration: 1.6, times: [0, 0.45, 1], ease: 'easeOut', delay: 0.3 }
              }
            />

            {/* Climax overlays — re-keyed on `shockwave` so each increment
                replays the flash + expanding rings in sync with a burst. */}
            {shockwave > 0 && (
              <motion.div
                key={`flash-${shockwave}`}
                className="absolute inset-[-40%] rounded-full pointer-events-none z-20"
                style={{ background: 'radial-gradient(circle, rgba(255,238,200,0.55) 0%, rgba(240,165,0,0.35) 28%, rgba(108,92,231,0.22) 50%, transparent 72%)' }}
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: [0, 0.95, 0], scale: [0.3, 1.5, 2] }}
                transition={{ duration: 1.1, ease: 'easeOut', times: [0, 0.25, 1] }}
              />
            )}
            {shockwave > 0 && [0, 1, 2].map((i) => (
              <motion.div
                key={`ring-${shockwave}-${i}`}
                className="absolute inset-0 rounded-full border-2 pointer-events-none z-20"
                style={{ borderColor: i % 2 ? 'rgba(240,165,0,0.55)' : 'rgba(108,92,231,0.55)' }}
                initial={{ opacity: 0.75, scale: 0.4 }}
                animate={{ opacity: 0, scale: 2.6 }}
                transition={{ duration: 1.3, delay: i * 0.13, ease: 'easeOut' }}
              />
            ))}

            {/* Mount the particle orb only once the hero is revealed so its
                scatter → converge → ignite assembly plays in full view (it is
                keyed to mount time), not hidden behind the intro. */}
            {orbReady && (
              <motion.div
                ref={orbRef}
                onClick={fireBurst}
                className="relative z-10 flex items-center justify-center cursor-pointer drop-shadow-[0_0_20px_rgba(108,92,231,0.2)]"
                style={{ width: 360, height: 360 }}
                animate={settled ? { rotate: 360 } : { rotate: 0 }}
                transition={settled ? { duration: 36, repeat: Infinity, ease: 'linear' } : { duration: 0 }}
              >
                <ParticleFingerprint
                  active
                  alive
                  cursorBias={cursorBias}
                  clickAction={clickAction}
                  size={360}
                  canvasSize={480}
                />
              </motion.div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="flex flex-col items-center gap-4"
          >
            <Link to="/signup">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }} className="inline-block">
                <Button variant="gold" size="lg">
                  Start free — 2 minutes
                  <ArrowRight size={16} />
                </Button>
              </motion.div>
            </Link>
            <p className="text-xs text-content-muted font-body">
              No credit card · 4-dimension profile · 1 free upload
            </p>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="pb-8 flex flex-col items-center gap-2 relative"
        initial={{ opacity: 0 }}
        animate={introDone ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M5 8L10 13L15 8" stroke="#F0A500" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ───────────────── PROBLEM (split layout) ───────────────── */
function ProblemSection() {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <Aurora palette="indigo" intensity={0.5} />
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 items-center relative">
        <Reveal variants={fadeRightItem} className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-pill text-xs font-semibold bg-error/10 text-error border border-error/20">
            The problem
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-6 leading-tight">
            You already have the material.<br />
            <span className="text-content-secondary">What you don't have is a way to learn it that works for you.</span>
          </h2>
          <div className="space-y-5 text-content-secondary font-body leading-relaxed">
            <p>
              You've read the chapter three times and still can't explain it. You've highlighted half the textbook and remember none of it. You've asked ChatGPT to summarise your notes and got a wall of generic bullet points that missed the point entirely.
            </p>
            <p>
              The problem isn't effort. It's that every study tool treats you like every other student. Same summaries. Same flashcards. Same pace.
            </p>
            <p className="text-content">
              Your brain has a <span className="text-gold-400 font-semibold">fingerprint</span>. Your study tools should know what it looks like.
            </p>
          </div>
        </Reveal>

        <Reveal variants={fadeLeftItem} className="lg:col-span-6">
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {/* "Generic" output */}
              <div
                className="relative overflow-hidden rounded-xl border border-error/25 p-4 pt-7 h-full"
                style={THICK_TILE_STYLE}
              >
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-pill text-[10px] font-semibold bg-error/20 text-error">
                  Generic AI
                </div>
                <p className="text-xs font-body text-content-secondary mb-3">Same output, every learner</p>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-2 rounded bg-white/15 mb-2" style={{ width: `${100 - i * 8}%` }} />
                ))}
                <div className="mt-3 flex items-center gap-1 opacity-70">
                  <div className="w-1 h-1 rounded-full bg-white/50" />
                  <div className="w-1 h-1 rounded-full bg-white/50" />
                  <div className="w-1 h-1 rounded-full bg-white/50" />
                </div>
              </div>

              {/* CogniPrint output */}
              <MagneticCard glow="indigo" className="h-full">
                <div
                  className="relative overflow-hidden rounded-xl border border-indigo-500/40 p-4 pt-7 gradient-border h-full"
                  style={THICK_TILE_STYLE}
                >
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded-pill text-[10px] font-semibold bg-indigo-500/20 text-indigo-300">
                    CogniPrint
                  </div>
                  <p className="text-xs font-body text-content-secondary mb-3">Shaped by your profile</p>
                  <div className="space-y-2">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '100%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.1 }}
                      className="h-2 rounded bg-gradient-to-r from-indigo-500 to-indigo-400"
                    />
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '85%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.25 }}
                      className="h-2 rounded bg-gradient-to-r from-indigo-400 to-gold-500"
                    />
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '70%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.4 }}
                      className="h-2 rounded bg-gradient-to-r from-gold-500 to-gold-400"
                    />
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <Sparkles size={10} className="text-gold-400" />
                    <span className="text-[10px] font-body text-content-muted">Profile-tuned</span>
                  </div>
                </div>
              </MagneticCard>
            </div>

            {/* Connector line */}
            <svg viewBox="0 0 200 60" className="w-full h-12 mt-4 opacity-50">
              <motion.path
                d="M50 10 Q 100 50, 150 10"
                stroke="url(#connector)"
                strokeWidth="1.5"
                fill="none"
                strokeDasharray="4 4"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2 }}
              />
              <defs>
                <linearGradient id="connector" x1="0" x2="1">
                  <stop offset="0" stopColor="#6C5CE7" />
                  <stop offset="1" stopColor="#F0A500" />
                </linearGradient>
              </defs>
            </svg>

            <p className="text-center text-xs font-body text-content-muted">
              Same content. Different learners. Different paths.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────── THREE STEPS (timeline + magnetic cards) ───────────────── */
function ThreeStepsSection() {
  const steps = [
    {
      icon: Fingerprint,
      number: '01',
      overline: 'STEP 1',
      title: 'Discover how you learn',
      subtitle: 'The CogniPrint Diagnostic',
      body: 'Answer a short set of questions — about 2 minutes — and CogniPrint maps your learning fingerprint across 4 core dimensions. Upgrade to unlock all 8 dimensions for deeper personalisation.',
      accent: 'indigo' as const,
    },
    {
      icon: BookOpen,
      number: '02',
      overline: 'STEP 2',
      title: 'Upload your content',
      subtitle: 'Your textbook, your terms',
      body: 'Drop in the PDF, document, or audio you need to learn. CogniPrint extracts every key concept and builds a connected knowledge graph of how everything fits together.',
      accent: 'mixed' as const,
    },
    {
      icon: Sparkles,
      number: '03',
      overline: 'STEP 3',
      title: 'Learn your way',
      subtitle: 'AI-powered. Personally yours.',
      body: 'Your content rebuilt for your CogniPrint. Visual learner? Concept maps first. Hands-on? Worked examples. Need depth? You get it. Professor Cogni adapts at every step.',
      accent: 'gold' as const,
    },
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <Aurora palette="mixed" intensity={0.6} />
      <div className="max-w-6xl mx-auto relative">
        <Reveal className="text-center mb-16">
          <span className="inline-block mb-4 px-3 py-1 rounded-pill text-xs font-semibold bg-indigo-500/12 text-indigo-300 border border-indigo-500/30">
            How it works
          </span>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
            Three steps. Five minutes to your first one.
          </h2>
          <p className="text-content-secondary font-body max-w-xl mx-auto">
            From a flat textbook to a learning experience built for your brain — start free, no credit card.
          </p>
        </Reveal>

        <div className="relative">
          {/* Connecting line behind cards (desktop) */}
          <svg
            className="hidden md:block absolute top-[60px] left-[16%] right-[16%] h-1 pointer-events-none"
            viewBox="0 0 100 4"
            preserveAspectRatio="none"
            style={{ height: 2 }}
          >
            <motion.line
              x1="0" y1="2" x2="100" y2="2"
              stroke="url(#stepLine)"
              strokeWidth="2"
              strokeDasharray="3 3"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, delay: 0.3 }}
            />
            <defs>
              <linearGradient id="stepLine" x1="0" x2="1">
                <stop offset="0" stopColor="#6C5CE7" />
                <stop offset="0.5" stopColor="#A594F9" />
                <stop offset="1" stopColor="#F0A500" />
              </linearGradient>
            </defs>
          </svg>

          <StaggerGroup className="grid md:grid-cols-3 gap-6 relative">
            {steps.map((step) => {
              const stepBorder =
                step.accent === 'gold'
                  ? 'border-gold-500/25'
                  : step.accent === 'mixed'
                    ? 'border-indigo-400/20'
                    : 'border-indigo-500/25';
              const stepNumberColor =
                step.accent === 'gold'
                  ? 'text-gold-500/30'
                  : step.accent === 'mixed'
                    ? 'text-indigo-300/30'
                    : 'text-indigo-400/30';
              return (
              <motion.div key={step.number} variants={scaleItem} className="h-full">
                <MagneticCard glow={step.accent} className="h-full">
                  <div
                    className={`h-full relative overflow-hidden rounded-xl border ${stepBorder} p-6 group`}
                    style={THICK_TILE_STYLE}
                  >
                    <div className={`absolute top-2 right-3 text-[64px] font-heading font-extrabold ${stepNumberColor} leading-none select-none pointer-events-none tracking-tight`}>
                      {step.number}
                    </div>
                    <div className={`relative w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${
                      step.accent === 'gold'
                        ? 'bg-gradient-to-br from-gold-400 to-gold-600 shadow-glow-gold'
                        : step.accent === 'mixed'
                          ? 'bg-gradient-to-br from-indigo-500 to-gold-500'
                          : 'bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-glow-indigo'
                    }`}>
                      <step.icon size={24} className="text-white" />
                      <motion.div
                        aria-hidden
                        className="absolute inset-0 rounded-xl border-2 border-white/20"
                        animate={{ scale: [1, 1.3], opacity: [0.6, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                      />
                    </div>
                    <p className="text-[10px] font-body font-bold tracking-[0.16em] uppercase text-content-muted mb-2">
                      {step.overline}
                    </p>
                    <h3 className="text-xl font-heading font-semibold mb-1 group-hover:text-indigo-300 transition-colors">{step.title}</h3>
                    <p className="text-sm font-body text-gold-400 mb-3">{step.subtitle}</p>
                    <p className="text-content-secondary text-sm leading-relaxed font-body">{step.body}</p>
                  </div>
                </MagneticCard>
              </motion.div>
              );
            })}
          </StaggerGroup>
        </div>
      </div>
    </section>
  );
}

/* ───────────────── KEY FEATURES (alternating) ───────────────── */
function KeyFeaturesSection() {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <Aurora palette="mixed" intensity={0.4} />
      <div className="max-w-6xl mx-auto space-y-24 relative">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="inline-block mb-4 px-3 py-1 rounded-pill text-xs font-semibold bg-gold-500/12 text-gold-400 border border-gold-500/30">
            What's inside
          </span>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
            Built around how your brain actually works.
          </h2>
        </Reveal>

        {/* Feature 1 — Profile (text left, visual right) */}
        <FeatureRow
          reverse={false}
          icon={Eye}
          overline="EIGHT DIMENSIONS. ONE LEARNING FINGERPRINT."
          title="Your CogniPrint Profile"
          body="CogniPrint maps your learning profile across eight dimensions: how you absorb information, how deeply you need to go, how you sequence ideas, what drives you, and more. This isn't a label — it's a living profile that shapes every learning experience CogniPrint creates for you."
          visual={<RadarMockup />}
        />

        {/* Feature 2 — Tutor (visual left, text right) */}
        <FeatureRow
          reverse
          icon={Brain}
          overline="CONTEXT-AWARE. ALWAYS AVAILABLE. SHAPED BY YOUR COGNIPRINT."
          title="Meet Professor Cogni — your personal AI tutor"
          body="Stuck on a concept at 10pm on a Tuesday? Professor Cogni is there. He knows your uploaded content, your CogniPrint profile, and exactly where you are in the material. Ask him to explain differently, go deeper, test your understanding, or connect ideas across topics."
          visual={<TutorMockup />}
        />

        {/* Feature 3 — Adaptive (text left, visual right) */}
        <FeatureRow
          reverse={false}
          icon={Zap}
          overline="ADAPTIVE. CONTINUOUS. INVISIBLE."
          title="Gets smarter the more you learn"
          body="CogniPrint doesn't just set your learning path and forget about it. As you study, the adaptive engine tracks how you're engaging — which concepts click, where you struggle, how your understanding evolves. It quietly recalibrates your learning path so the experience keeps getting better."
          visual={<AdaptiveMockup />}
        />

        {/* Feature 4 — Recalibration (visual left, text right) with Coach footnote */}
        <FeatureRow
          reverse
          icon={RefreshCw}
          overline="RECALIBRATES EVERY TWO WEEKS. ADAPTS TO THE LEARNER YOU BECOME."
          title="Your CogniPrint evolves with you"
          body="Most study tools set you up once and assume you never change. CogniPrint knows better. Every two weeks, your profile quietly recalibrates against how you actually studied — which concepts clicked, which Tutor modes you used most, how your mastery shifted. You get a short monthly report that shows what moved and what it means."
          footnote="Coach add-on ($7.99/mo on Pro) turns recalibration into a conversation: bi-weekly reviews, unlimited check-ins, and written guidance tied to your CogniPrint."
          visual={<RecalibrationMockup />}
        />
      </div>
    </section>
  );
}

type FeatureRowProps = {
  reverse: boolean;
  icon: typeof Eye;
  overline: string;
  title: string;
  body: string;
  footnote?: string;
  visual: React.ReactNode;
};
function FeatureRow({ reverse, icon: Icon, overline, title, body, footnote, visual }: FeatureRowProps) {
  return (
    <div className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? 'lg:[direction:rtl]' : ''}`}>
      <Reveal variants={reverse ? fadeLeftItem : fadeRightItem} className="lg:col-span-6 [direction:ltr]">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-5 shadow-glow-indigo">
          <Icon size={22} className="text-white" />
        </div>
        <p className="text-[10px] font-body font-bold tracking-[0.16em] uppercase text-gold-400 mb-2">{overline}</p>
        <h3 className="font-heading font-semibold text-2xl sm:text-3xl mb-4 leading-tight">{title}</h3>
        <p className="text-content-secondary leading-relaxed font-body">{body}</p>
        {footnote ? (
          <div
            className="mt-5 inline-flex items-start gap-2 px-4 py-3 rounded-lg border border-gold-500/30 text-gold-400 font-body text-sm leading-relaxed relative overflow-hidden"
            style={THICK_TILE_STYLE}
          >
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" />
            <span>{footnote}</span>
          </div>
        ) : null}
      </Reveal>
      <Reveal variants={reverse ? fadeRightItem : fadeLeftItem} className="lg:col-span-6 [direction:ltr]">
        <MagneticCard glow="mixed">
          <div
            className="relative overflow-hidden rounded-2xl border border-indigo-400/15 p-6"
            style={THICK_TILE_STYLE}
          >
            {visual}
          </div>
        </MagneticCard>
      </Reveal>
    </div>
  );
}

/* ───────────────── Feature visuals ───────────────── */
function RadarMockup() {
  const dims = [
    { label: 'Modality', value: 0.85, color: '#6C5CE7' },
    { label: 'Depth', value: 0.92, color: '#F0A500' },
    { label: 'Sequencing', value: 0.6, color: '#55EFC4' },
    { label: 'Approach', value: 0.7, color: '#A594F9' },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-heading font-semibold">Your profile</span>
        <span className="px-2 py-0.5 rounded-pill text-[10px] font-semibold bg-indigo-500/15 text-indigo-300">4 of 8 unlocked</span>
      </div>
      <div className="space-y-3">
        {dims.map((d, i) => (
          <div key={d.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-body text-content-secondary">{d.label}</span>
              <span className="text-xs font-mono text-content-muted">{Math.round(d.value * 100)}%</span>
            </div>
            <div className="h-2 rounded-full bg-dark-bg overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${d.color}, ${d.color}AA)` }}
                initial={{ width: 0 }}
                whileInView={{ width: `${d.value * 100}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-dark-border">
        <p className="text-xs font-body text-content-muted leading-relaxed">
          You prefer <span className="text-indigo-300">visual formats</span>, go <span className="text-gold-400">deep</span>, and start with the <span className="text-indigo-300">big picture</span>.
        </p>
      </div>
    </div>
  );
}

function TutorMockup() {
  const modes = ['Explain', 'Go Deeper', 'Test Me', 'Connect', 'Simplify'];
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center">
          <Brain size={16} className="text-white" />
        </div>
        <div>
          <div className="font-heading font-semibold text-sm">Professor Cogni</div>
          <div className="flex items-center gap-1.5 text-[10px] font-body text-success">
            <span className="w-1.5 h-1.5 rounded-full bg-success" /> Online · knows your profile
          </div>
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <div className="ml-auto max-w-[80%] rounded-lg rounded-tr-sm bg-indigo-500/15 border border-indigo-500/25 p-3 text-xs font-body text-content">
          Explain depreciation differently — I'm a visual learner.
        </div>
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="max-w-[88%] rounded-lg rounded-tl-sm bg-dark-bg border border-dark-border p-3 text-xs font-body text-content-secondary leading-relaxed"
        >
          Picture an asset as a <span className="text-gold-400 font-semibold">leaking bucket</span>. Each year a slice drips out — that drip is depreciation. Want me to sketch the bucket as a concept map?
        </motion.div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {modes.map((m, i) => (
          <span key={m} className={`px-2.5 py-1 rounded-pill text-[10px] font-body ${i === 0 ? 'bg-indigo-500 text-white' : 'bg-dark-bg border border-dark-border text-content-muted'}`}>{m}</span>
        ))}
      </div>
    </div>
  );
}

function AdaptiveMockup() {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-heading font-semibold text-sm">Path adaptation</span>
        <span className="text-[10px] font-mono text-content-muted">live</span>
      </div>
      <div className="space-y-3">
        {[
          { label: 'Concept: Cash flows', shift: '+12%', color: 'success' },
          { label: 'Tutor mode: Test Me', shift: '+8%', color: 'indigo' },
          { label: 'Mastery: Ratios', shift: 'mastered', color: 'gold' },
          { label: 'Reading pace', shift: '−4%', color: 'muted' },
        ].map((row, i) => (
          <motion.div
            key={row.label}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12 }}
            className="flex items-center justify-between p-2.5 rounded-lg bg-dark-bg border border-dark-border"
          >
            <span className="text-xs font-body text-content-secondary">{row.label}</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-pill ${
              row.color === 'success' ? 'bg-success/15 text-success' :
              row.color === 'gold' ? 'bg-gold-500/15 text-gold-400' :
              row.color === 'indigo' ? 'bg-indigo-500/15 text-indigo-300' :
              'bg-dark-border text-content-muted'
            }`}>{row.shift}</span>
          </motion.div>
        ))}
      </div>
      <p className="mt-4 text-[11px] font-body text-content-muted">
        Your learning path updated — 3 concepts reinforced, 1 new connection added.
      </p>
    </div>
  );
}

function RecalibrationMockup() {
  const weeks = [
    { label: 'Week 0', y: [70, 60, 75, 80] },
    { label: 'Week 2', y: [78, 62, 78, 84] },
    { label: 'Week 4', y: [86, 70, 76, 90] },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-heading font-semibold text-sm">Profile drift</span>
        <span className="px-2 py-0.5 rounded-pill text-[10px] font-semibold bg-gold-500/15 text-gold-400">recalibrated</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {weeks.map((w, i) => (
          <motion.div
            key={w.label}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.18 }}
            className="rounded-lg bg-dark-bg border border-dark-border p-3"
          >
            <p className="text-[10px] font-body text-content-muted mb-2">{w.label}</p>
            <div className="flex items-end gap-1 h-14">
              {w.y.map((v, j) => (
                <div key={j} className="flex-1 bg-gradient-to-t from-indigo-500 to-indigo-300 rounded-sm" style={{ height: `${v}%` }} />
              ))}
            </div>
          </motion.div>
        ))}
      </div>
      <div className="rounded-lg p-3 bg-gold-500/8 border border-gold-500/20">
        <div className="text-[10px] font-body font-bold tracking-wider uppercase text-gold-400 mb-1">Monthly report preview</div>
        <p className="text-xs font-body text-content-secondary leading-relaxed">
          Two dimensions shifted: you're going deeper than at signup, and your sequencing preference flipped to bottom-up. Your next path will start with worked examples.
        </p>
      </div>
    </div>
  );
}

/* ───────────────── PROFILE TEASER (CTA with glow + dimension reveal) ───────────────── */
function ProfileTeaserSection() {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <RidgeTexture opacity="opacity-[0.07]" />
      <Aurora palette="gold" intensity={0.7} />
      <div className="max-w-4xl mx-auto relative">
        <Reveal variants={scaleItem}>
          <GlowBorder
            rounded="rounded-2xl"
            className="border border-gold-500/20 p-8 sm:p-12 text-center relative overflow-hidden"
            style={THICK_TILE_STYLE}
          >
            <span className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-pill text-xs font-semibold bg-gold-500/12 text-gold-400 border border-gold-500/30">
              <Sparkles size={12} /> Free forever
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-5">
              Your <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">CogniPrint</span> is waiting.
            </h2>
            <p className="text-content-secondary font-body leading-relaxed mb-8 max-w-2xl mx-auto">
              In about 2 minutes, CogniPrint maps how your brain processes, retains, and connects information across 4 core dimensions. Upgrade to unlock all 8.
            </p>

            <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8 text-left max-w-2xl mx-auto">
              {dimensions.map((d, i) => (
                <motion.div
                  key={d}
                  variants={fadeUpItem}
                  className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-dark-bg/50 border border-dark-border"
                >
                  <span className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-mono font-bold ${i < 4 ? 'bg-gold-500/20 text-gold-400' : 'bg-dark-border text-content-muted'}`}>
                    {i + 1}
                  </span>
                  <span className={`text-xs font-body ${i < 4 ? 'text-content' : 'text-content-muted'}`}>{d}</span>
                </motion.div>
              ))}
            </StaggerGroup>
            <p className="text-[11px] font-body text-content-muted mb-6">
              <span className="text-gold-400">●</span> Free tier · <span className="text-content-dim">●</span> Unlocked with a paid plan
            </p>

            <Link to="/signup">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }} className="inline-block">
                <Button variant="gold" size="lg">
                  Discover How You Learn — Free
                  <ArrowRight size={16} />
                </Button>
              </motion.div>
            </Link>
          </GlowBorder>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────── SOCIAL PROOF (with metrics) ───────────────── */
function SocialProofSection() {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <Aurora palette="cool" intensity={0.4} />
      <div className="max-w-5xl mx-auto relative">
        <Reveal className="text-center mb-12">
          <p className="text-content-secondary font-body">
            CogniPrint is in private beta. Early access members are helping shape the future of personalised learning.
          </p>
        </Reveal>

        <StaggerGroup className="grid sm:grid-cols-3 gap-4 mb-12">
          {[
            { stat: 250, suffix: '+', label: 'Waitlist signups' },
            { stat: 8, suffix: '', label: 'Profile dimensions' },
            { stat: 5, suffix: '', label: 'Tutor interaction modes' },
          ].map((m) => (
            <motion.div
              key={m.label}
              variants={fadeUpItem}
              className="relative overflow-hidden rounded-xl border border-indigo-400/15 p-5 text-center"
              style={THICK_TILE_STYLE}
            >
              <div className="font-heading font-extrabold text-3xl mb-1 bg-gradient-to-r from-indigo-300 to-gold-400 bg-clip-text text-transparent">
                <AnimatedNumber value={m.stat} />{m.suffix}
              </div>
              <div className="text-xs font-body text-content-muted">{m.label}</div>
            </motion.div>
          ))}
        </StaggerGroup>

        <StaggerGroup className="grid md:grid-cols-3 gap-5">
          {[
            { role: 'SA university student', quote: 'Testimonial about personalisation during exam prep coming soon.' },
            { role: 'Professional cert candidate', quote: 'Testimonial about depth and cross-topic connections coming soon.' },
            { role: 'Self-directed learner', quote: 'Testimonial about having a study partner at any hour coming soon.' },
          ].map((t) => (
            <motion.div key={t.role} variants={fadeUpItem}>
              <MagneticCard glow="indigo">
                <div
                  className="h-full relative overflow-hidden rounded-xl border border-indigo-500/20 p-6"
                  style={THICK_TILE_STYLE}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white text-sm font-bold">
                      {t.role[0]}
                    </div>
                    <span className="text-sm font-body text-content-muted">{t.role}</span>
                  </div>
                  <p className="text-sm text-content-secondary font-body italic leading-relaxed">"{t.quote}"</p>
                </div>
              </MagneticCard>
            </motion.div>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* ───────────────── MINI FAQ ───────────────── */
function MiniFaqSection() {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <Aurora palette="indigo" intensity={0.4} />
      <div className="max-w-3xl mx-auto relative">
        <Reveal className="text-center mb-10">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl">Frequently asked questions</h2>
        </Reveal>
        <Reveal>
          <Accordion items={miniFaq} />
        </Reveal>
        <Reveal className="text-center mt-8">
          <Link to="/how-it-works" className="inline-flex items-center gap-1 text-sm font-body text-indigo-400 hover:text-indigo-300 transition-colors">
            See the full FAQ on /how-it-works <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────── FINAL CTA ───────────────── */
function FinalCtaSection() {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <RidgeTexture opacity="opacity-[0.07]" />
      <Aurora palette="mixed" intensity={0.9} />
      <div className="max-w-4xl mx-auto relative">
        <Reveal variants={scaleItem}>
          <GlowBorder
            rounded="rounded-2xl"
            className="border border-indigo-400/20 p-8 sm:p-14 text-center relative overflow-hidden"
            style={THICK_TILE_STYLE}
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-8 leading-tight">
              Your brain has a fingerprint.
              <br />
              <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">
                It's time your study tools knew what it looked like.
              </span>
            </h2>
            <div className="flex flex-col items-center gap-6">
              <Link to="/signup">
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }} className="inline-block">
                  <Button variant="gold" size="lg">
                    Start free — 2 minutes
                    <ArrowRight size={16} />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </GlowBorder>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────── PAGE ───────────────── */
function useLiteMode() {
  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const saveData = typeof navigator !== 'undefined' && (() => {
    const c = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;
    if (!c) return false;
    if (c.saveData) return true;
    if (c.effectiveType === '2g' || c.effectiveType === 'slow-2g') return true;
    return false;
  })();
  return prefersReducedMotion || saveData;
}

export function Landing() {
  const lite = useLiteMode();
  return (
    <div className="min-h-screen bg-dark-bg relative overflow-hidden">
      {/* Skip ambient layers on reduced-motion / save-data */}
      {!lite && (
        <>
          <ScrollVeil />
          <FloatingParticles count={10} opacity={0.14} />
          <CursorGlow />
        </>
      )}

      <Header />
      <Hero />
      <SectionDivider />
      <ProblemSection />
      <SectionDivider />
      <ThreeStepsSection />
      <SectionDivider />
      <KeyFeaturesSection />
      <SectionDivider />
      <ProfileTeaserSection />
      <SectionDivider />
      <SocialProofSection />
      <SectionDivider />
      <MiniFaqSection />
      <SectionDivider />
      <FinalCtaSection />
      <Footer />
    </div>
  );
}

// Suppress unused — kept for type continuity with depth helpers.
void FileText; void MessageSquare; void Layers; void Target;
